import { Box, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { AiEngineList, MenuList } from "../../../../context/juno/VideoContext";
import { RowBox } from "../../../../utils/styledBox";
import { getIcon } from "./utils";
import { Info } from "@mui/icons-material";

interface ModelVersionSelectBoxProps {
  platform: AiEngineList;
  value: string;
  onChange: (value: string) => void;
  menu: MenuList;
}

const ModelVersionSelectBox: React.FC<ModelVersionSelectBoxProps> = ({ platform, value, onChange, menu }) => {
  const { t } = useTranslation();

  // ラベルの取得
  const getLabel = (version: string) => {
    return t(`juno.video.ai.${platform.toLowerCase()}.model.${version}`);
  };

  // モデル制限情報をネストされた構造で管理
  const modelRestrictions = {
    // PIKAモデルの制限
    PIKA: [
      // V2_2_SCENES_720P: i2vのみ許可
      {
        model: "V2_2_SCENES_720P",
        disableMenus: ["t2v", "v2v"],
        fallbackModel: "V2_2_1080P", // t2vでは標準モデルに変更
      },
      // V2_2_SCENES_1080P: i2vのみ許可
      {
        model: "V2_2_SCENES_1080P",
        disableMenus: ["t2v", "v2v"],
        fallbackModel: "V2_2_1080P", // t2vでは標準モデルに変更
      },
      // V2_SWAPS: v2vのみ許可
      {
        model: "V2_SWAPS",
        disableMenus: ["t2v", "i2v"],
        fallbackModel: "V2_2_1080P", // t2vでは標準モデルに変更
      },
      // V2_ADDITIONS: v2vのみ許可
      {
        model: "V2_ADDITIONS",
        disableMenus: ["t2v", "i2v"],
        fallbackModel: "V2_2_1080P", // t2vでは標準モデルに変更
      },
    ],
    // HAILUOモデルの制限
    HAILUO: [
      // MINI_MAX_REF: t2vでは選択不可
      {
        model: "MINI_MAX_REF",
        disableMenus: ["t2v"],
        fallbackModel: "MINI_MAX_LIVE",
      },
    ],
    // 他のAIプラットフォームの制限を追加できる
  };

  // 選択不可の判定
  const isDisabled = (version: string): boolean => {
    // PIKAのv2vモードは特殊ケース（V2_SWAPSとV2_ADDITIONSのみ許可）
    if (platform === "PIKA" && menu === "v2v") {
      return version !== "V2_SWAPS" && version !== "V2_ADDITIONS";
    }

    // モデル制限情報を確認
    const platformRestrictions = modelRestrictions[platform as keyof typeof modelRestrictions];
    if (platformRestrictions) {
      // プラットフォームに対する制限を取得
      const restriction = platformRestrictions.find((r) => r.model === version);
      // 制限がある場合、現在のメニューが無効リストに含まれているか確認
      if (restriction && restriction.disableMenus.includes(menu)) {
        return true;
      }
    }

    return false;
  };

  // デフォルトバージョンの取得
  const getDefaultVersion = (currentVersion: string): string => {
    // PIKAのv2vモードは特殊ケース
    if (platform === "PIKA" && menu === "v2v") {
      // V2_SWAPS または V2_ADDITIONS 以外の場合は V2_SWAPS をデフォルトに
      if (currentVersion !== "V2_SWAPS" && currentVersion !== "V2_ADDITIONS") {
        return "V2_SWAPS";
      }
    } else if (platform === "PIKA" && menu === "i2v") {
      // i2vモードでSCENES系が選択可能だが、現在V2_SWAPSなどが選択されている場合
      if (currentVersion === "V2_SWAPS" || currentVersion === "V2_ADDITIONS") {
        return "V2_2_SCENES_720P"; // SCENESモデルをデフォルトに
      }
    }

    // モデル制限情報を確認
    const platformRestrictions = modelRestrictions[platform as keyof typeof modelRestrictions];
    if (platformRestrictions) {
      // 現在のバージョンに対する制限を取得
      const restriction = platformRestrictions.find((r) => r.model === currentVersion);
      // 制限がある場合で、現在のメニューが無効リストに含まれている場合はフォールバックモデルを返す
      if (restriction && restriction.disableMenus.includes(menu)) {
        return restriction.fallbackModel;
      }
    }

    return currentVersion;
  };

  // 初期値の自動変更
  useEffect(() => {
    const defaultVersion = getDefaultVersion(value);
    if (defaultVersion !== value) {
      onChange(defaultVersion);
    }
  }, [menu, value, onChange]);

  const renderMenuItem = (version: string) => {
    const icon = getIcon(version);
    const label = getLabel(version);
    const description = t(`juno.video.ai.${platform.toLowerCase()}.model.${version}_description`);

    return (
      <MenuItem key={version} value={version} disabled={isDisabled(version)}>
        <RowBox sx={{ gap: 1 }}>
          {icon}
          <Typography variant="body1">{label}</Typography>
          <Box flexGrow={1} />
          <Tooltip title={<Typography variant="body2">{description}</Typography>} placement="right" followCursor>
            <Info fontSize="small" />
          </Tooltip>
        </RowBox>
      </MenuItem>
    );
  };

  const getModelVersions = () => {
    switch (platform) {
      case "PIXVERSE":
        return [
          "V3_5",
          "V3_5_1080P",
          "V3_5_FAST",
          "V3_5_TRANSITION",
          "V3_5_TRANSITION_1080P",
          "V4",
          "V4_1080P",
          "V4_FAST",
        ];
      case "HAILUO":
        return ["MINI_MAX_LIVE", "MINI_MAX_REF", "MINI_MAX_DIRECTOR", "MINI_MAX"];
      case "HAIPER":
        return ["V2_0", "V2_5"];
      case "KLING":
        return ["V1_0_STANDARD", "V1_0_PRO", "V1_5_PRO", "V1_6_STANDARD", "V1_6_PRO", "V2_MASTER"];
      case "LUMA":
        return ["RAY_1_6", "RAY_2", "RAY_2_1080P", "RAY_2_4K", "RAY_2_FLASH", "RAY_2_FLASH_1080P", "RAY_2_FLASH_4K"];
      case "RUNWAY":
        return ["GEN3_ALPHA_TURBO", "GEN4_TURBO"];
      case "VIDU":
        return ["VIDU_2_0_720P", "VIDU_2_0_1080P", "VIDU_2_0_REF_720P"];
      case "SYNC":
        return ["LIPSYNC_190", "LIPSYNC_V2"];
      case "BYTEDANCE":
        return ["LATENTSYNC"];
      case "REALESRGAN":
        return ["REALESRGAN"];
      case "GOOGLE":
        return ["VEO2"];
      case "WAN":
        return ["V2_1", "V2_1_PRO", "FIRST_TO_LAST"];
      case "PIKA":
        return [
          "V2_2_720P",
          "V2_2_1080P",
          "V2_2_SCENES_720P",
          "V2_2_SCENES_1080P",
          "V2_TURBO_720P",
          "V2_SWAPS",
          "V2_ADDITIONS",
        ];
      case "TOPAZLABS":
        return ["TOPAZ_VIDEO_UPSCALE"];
      default:
        return [];
    }
  };

  return (
    <Select value={value} onChange={(e) => onChange(e.target.value)} variant="standard" fullWidth>
      {getModelVersions().map((version) => renderMenuItem(version))}
    </Select>
  );
};

export default ModelVersionSelectBox;
